import React from "react";
import { Coffee, Rocket, AlertTriangle, Check, X, Clock, TrendingUp } from "lucide-react";

/**
 * Live status pill for a published alert.
 *
 * Backend `live` field shape (see /api/alerts/feed):
 *   status        : ACTIVE | WIN_TP | WIN_MOON | LOSS_SL | WIN_TIME | LOSS_TIME | UNKNOWN
 *   return_pct    : live P&L since entry
 *   entry_zone    : in_zone | extended | chasing | below   (only when ACTIVE)
 *   tp1_hit       : bool (relevant for ACTIVE alerts — runner phase)
 *   days_remaining: int  (only ACTIVE)
 *   days_elapsed  : int
 *   max_hold_days : int
 */
const STATUS_MAP = {
  WIN_MOON:  { label: "MOON",      tone: "moon",   Icon: Rocket },
  WIN_TP:    { label: "WIN",       tone: "win",    Icon: Check },
  WIN_TIME:  { label: "WIN · TIME",tone: "win",    Icon: Check },
  LOSS_SL:   { label: "STOPPED",   tone: "loss",   Icon: X },
  LOSS_TIME: { label: "EXPIRED",   tone: "expire", Icon: Clock },
};

const ZONE_MAP = {
  in_zone:  { label: "IN ZONE",  tone: "fresh",   Icon: TrendingUp },
  extended: { label: "EXTENDED", tone: "warn",    Icon: AlertTriangle },
  chasing:  { label: "CHASING",  tone: "chase",   Icon: AlertTriangle },
  below:    { label: "BELOW",    tone: "neutral", Icon: Coffee },
};

const TONE_STYLES = {
  moon:    "bg-gradient-to-r from-pink-500 to-pink-700 text-white",
  win:     "bg-emerald-100 text-emerald-800",
  loss:    "bg-red-100 text-red-700",
  expire:  "bg-neutral-200 text-neutral-600",
  fresh:   "bg-mb-emerald text-white",
  warn:    "bg-amber-100 text-amber-800",
  chase:   "bg-orange-50 text-orange-700 border border-orange-200",
  neutral: "bg-blue-50 text-blue-700",
};

export default function AlertStatusBadge({ live, alertClass, compact = false }) {
  if (!live) return null;
  // Closed status wins over zone (the trade already played out)
  let cfg = STATUS_MAP[live.status];
  if (!cfg && live.status === "ACTIVE") {
    cfg = ZONE_MAP[live.entry_zone] || ZONE_MAP.in_zone;
  }
  if (!cfg) return null;

  const Icon = cfg.Icon;
  const cls = TONE_STYLES[cfg.tone] || TONE_STYLES.neutral;
  const sizing = compact ? "px-2 py-0.5 text-[10px]" : "px-2.5 py-1 text-[11px]";
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full font-mono-data font-bold tracking-wider ${sizing} ${cls}`}
      data-testid={`alert-status-${live.status}-${live.entry_zone || "x"}`}
      title={tooltip(live, alertClass)}
    >
      <Icon className={compact ? "w-2.5 h-2.5" : "w-3 h-3"} />
      {cfg.label}
    </span>
  );
}

function tooltip(live, alertClass) {
  const ret = live.return_pct ?? 0;
  const days = live.days_remaining;
  if (live.status === "ACTIVE") {
    const zone = (live.entry_zone || "").toUpperCase();
    return `${zone} · Now ${ret >= 0 ? "+" : ""}${ret}% · ${days} days left`;
  }
  return `${live.status} · ${ret >= 0 ? "+" : ""}${ret}%`;
}

/** Compact live P&L pill — separate so we can render side-by-side with status badge.
 *  Shows both "since entry" and "today's move" so subscribers know exactly which
 *  is which (the two rarely match — a -6% since-entry could still be +2% today
 *  on a bounce, which is important context for hold-vs-cut decisions). */
export function LivePnLPill({ live }) {
  if (!live || live.return_pct == null) return null;
  const ret = live.return_pct;
  const day = live.day_change_pct;
  const pos = ret >= 0;
  const dayPos = day == null ? null : day >= 0;
  return (
    <span className="inline-flex items-center gap-2 font-mono-data" data-testid="live-pnl">
      <span
        className={`inline-flex items-baseline gap-1 ${pos ? "text-emerald-700" : "text-red-700"}`}
        title="Return since alert entry"
      >
        <span className="font-bold text-xs">{pos ? "+" : ""}{ret}%</span>
        <span className="text-[9px] uppercase tracking-widest opacity-70">since entry</span>
      </span>
      {day != null && (
        <span
          className={`inline-flex items-baseline gap-1 border-l pl-2 ${dayPos ? "text-emerald-600" : "text-red-600"}`}
          style={{ borderColor: "rgba(148,163,184,0.35)" }}
          title="Move today (previous close → last)"
          data-testid="live-day-change"
        >
          <span className="font-bold text-xs">{dayPos ? "+" : ""}{day}%</span>
          <span className="text-[9px] uppercase tracking-widest opacity-70">today</span>
        </span>
      )}
    </span>
  );
}

/** Day counter — "Day 3 · 12 left" — only meaningful on ACTIVE alerts. */
export function DaysRemainingPill({ live }) {
  if (!live || live.status !== "ACTIVE") return null;
  const remaining = live.days_remaining ?? 0;
  const elapsed = live.days_elapsed ?? 0;
  const max = live.max_hold_days ?? 15;
  const tight = remaining <= 2;
  return (
    <span
      className={`font-mono-data text-[10px] tracking-wider uppercase px-1.5 py-0.5 rounded ${tight ? "bg-amber-100 text-amber-800" : "bg-purple-100"}`}
      style={tight ? undefined : { color: "var(--mb-emerald-dark)" }}
      data-testid="days-remaining"
      title={`Day ${elapsed} of ${max}`}
    >
      {remaining}d left
    </span>
  );
}

/** Returns true if the live status is a closed outcome (used for History tab). */
export function isClosed(live) {
  if (!live) return false;
  return ["WIN_TP", "WIN_MOON", "WIN_TIME", "LOSS_SL", "LOSS_TIME"].includes(live.status);
}

/** Returns true if the alert is essentially stale (TP1 already hit or chasing) — show with less attention. */
export function isStale(live) {
  if (!live || live.status !== "ACTIVE") return false;
  return live.entry_zone === "chasing" || live.tp1_hit;
}
